import React, { useState } from "react";
import {
  Card,
  Input,
  Space,
  Button,
  message,
  Spin,
  Tooltip,
  Table,
  Radio,
  RadioChangeEvent,
  Collapse,
  Alert,
  Tabs,
  Select,
} from "antd";
import { QuestionCircleOutlined, DownloadOutlined, CopyOutlined } from "@ant-design/icons";

const { TextArea } = Input;

// API 配置
const MORALIS_API_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJub25jZSI6IjI1MDk1ZmExLTcxYjAtNDFjZC05NTI2LTAwYWYxMzE5MmRlMiIsIm9yZ0lkIjoiNDU5NjA5IiwidXNlcklkIjoiNDcyODU0IiwidHlwZUlkIjoiNTZlN2ExYzgtYTkwNC00YjNhLWI1NzgtNzk0NjE3ZjdhOWY2IiwidHlwZSI6IlBST0pFQ1QiLCJpYXQiOjE3NTI2NTgzMDAsImV4cCI6NDkwODQxODMwMH0.jS2uJhm8qhv12o0Q4o8sSGIw1MphmzRhRvUADB85Mns";
const ETHERSCAN_API_KEY = "MVPP4EJFKZ2D86FGNH21W6R385PK75A29Q"; // 备用API
const TARGET_METHOD_ID = "0xc8aa65c1";
const TX_COUNT = 50; // 查询最近50笔交易
const ETH_ADDRESS_REGEX = /^0x[a-fA-F0-9]{40}$/;
const MIN_BNB_THRESHOLD = 1;
const MIN_TOKEN_THRESHOLD = 1000; // ERC20最小阈值（1000 USDT/USDC）
const REQUEST_DELAY = 200; // Moralis API 允许更快的请求速率
const RETRY_DELAY = 3000; // 重试等待时间（毫秒）
const FETCH_TIMEOUT = 15000; // fetch超时时间（毫秒）

// BSC链上的ERC20代币合约地址
const TOKEN_CONTRACTS: Record<string, { address: string; decimals: number; symbol: string }> = {
  USDT: {
    address: "0x55d398326f99059fF775485246999027B3197955",
    decimals: 18,
    symbol: "USDT"
  },
  USDC: {
    address: "0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d",
    decimals: 18,
    symbol: "USDC"
  }
};

// API 提供商类型
type APIProvider = "moralis" | "etherscan";

// 缓存：币安Web3检测结果
const addressCache: Record<string, string> = {};

// 统计各API使用次数
let apiStats = {
  moralis: { success: 0, failed: 0 },
  etherscan: { success: 0, failed: 0 }
};

// 延迟函数
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// 带超时的fetch
const fetchWithTimeout = async (url: string, options?: RequestInit, timeout: number = FETCH_TIMEOUT) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    clearTimeout(timeoutId);
    return response;
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('请求超时');
    }
    throw error;
  }
};

// Moralis API: 获取地址交易列表
const getTransactionsFromMoralis = async (address: string): Promise<any[]> => {
  const url = `https://deep-index.moralis.io/api/v2/${address}?chain=bsc&limit=${TX_COUNT}`;

  const response = await fetchWithTimeout(url, {
    headers: {
      'X-API-Key': MORALIS_API_KEY,
      'Accept': 'application/json'
    }
  });

  if (!response.ok) {
    throw new Error(`Moralis API错误: ${response.status}`);
  }

  const data = await response.json();

  // Moralis 返回格式: { result: [...] }
  if (!data.result || !Array.isArray(data.result)) {
    throw new Error("Moralis API返回数据异常");
  }

  return data.result;
};

// Etherscan API: 获取地址交易列表
const getTransactionsFromEtherscan = async (address: string): Promise<any[]> => {
  const url = `https://api.etherscan.io/v2/api?chainid=56&module=account&action=txlist&address=${address}&startblock=0&endblock=99999999&page=1&offset=${TX_COUNT}&sort=desc&apikey=${ETHERSCAN_API_KEY}`;

  const response = await fetchWithTimeout(url);

  if (!response.ok) {
    throw new Error(`Etherscan API错误: ${response.status}`);
  }

  const data = await response.json();

  // 检查API是否返回错误
  if (data.status === "0" && data.message) {
    // 检查速率限制错误
    if (data.message.includes("rate limit") || data.message.includes("Max rate")) {
      throw new Error("API速率限制");
    }
    // 检查临时不可用错误（需要切换到备用API）
    if (data.message === "NOTOK" || data.message.includes("temporarily unavailable")) {
      throw new Error("Etherscan API临时不可用");
    }
    // "No transactions found" 是正常情况，不需要警告
  }

  // 如果没有result字段或result不是数组，视为API错误
  if (!data.result || !Array.isArray(data.result)) {
    // 特殊处理：如果是 "NOTOK" 但result为字符串（表示错误信息）
    if (data.status === "0" && typeof data.result === "string") {
      throw new Error(`Etherscan API错误: ${data.result}`);
    }
    throw new Error("Etherscan API返回数据异常");
  }

  return data.result;
};

// 通用交易获取函数（带API备选机制）
const getTransactions = async (address: string, preferredProvider: APIProvider = "moralis"): Promise<{ transactions: any[], provider: APIProvider }> => {
  // 先尝试首选API
  try {
    if (preferredProvider === "moralis") {
      const transactions = await getTransactionsFromMoralis(address);
      apiStats.moralis.success++;
      return { transactions, provider: "moralis" };
    } else {
      const transactions = await getTransactionsFromEtherscan(address);
      apiStats.etherscan.success++;
      return { transactions, provider: "etherscan" };
    }
  } catch (error: any) {
    if (preferredProvider === "moralis") {
      apiStats.moralis.failed++;
    } else {
      apiStats.etherscan.failed++;
    }

    // 尝试备选API
    const fallbackProvider = preferredProvider === "moralis" ? "etherscan" : "moralis";
    try {
      if (fallbackProvider === "moralis") {
        const transactions = await getTransactionsFromMoralis(address);
        apiStats.moralis.success++;
        return { transactions, provider: "moralis" };
      } else {
        const transactions = await getTransactionsFromEtherscan(address);
        apiStats.etherscan.success++;
        return { transactions, provider: "etherscan" };
      }
    } catch (fallbackError: any) {
      if (fallbackProvider === "moralis") {
        apiStats.moralis.failed++;
      } else {
        apiStats.etherscan.failed++;
      }
      throw new Error(`所有API均失败: ${error.message}, ${fallbackError.message}`);
    }
  }
};

// 检查是否为币安Web3钱包（内部函数，包含重试逻辑）
const checkAddressInternal = async (address: string, isRetry: boolean = false, preferredProvider: APIProvider = "moralis"): Promise<string> => {
  if (!ETH_ADDRESS_REGEX.test(address)) {
    return "格式错误";
  }

  try {
    const { transactions } = await getTransactions(address, preferredProvider);

    // 如果transactions是空数组，说明该地址在BSC上没有交易记录
    if (transactions.length === 0) {
      return "无交易记录";
    }

    // 检查所有相关交易（包括发起和接收）
    // Moralis: from_address/to_address + input, Etherscan: from/to + input
    const found = transactions.some(
      (tx: any) => {
        // 兼容 Moralis 的 from_address/to_address 和 Etherscan 的 from/to
        const fromAddr = tx.from_address || tx.from;
        const toAddr = tx.to_address || tx.to;
        const normalizedAddress = address.toLowerCase();

        const isFromAddress = fromAddr && fromAddr.toLowerCase() === normalizedAddress;
        const isToAddress = toAddr && toAddr.toLowerCase() === normalizedAddress;
        const isRelated = isFromAddress || isToAddress;

        if (isRelated) {
          // 兼容 Moralis 和 Etherscan 的 input 字段（都叫 input）
          const txData = tx.input || tx.data;

          if (txData) {
            return txData.startsWith(TARGET_METHOD_ID);
          }
        }
        return false;
      }
    );

    return found ? "是" : "否";
  } catch (e: any) {
    const errorMsg = e.message || "未知错误";
    console.error(`检测地址 ${address} 失败 (${isRetry ? '重试' : '首次'}): ${errorMsg}`);

    // 只有首次尝试时才抛出错误触发重试
    if (!isRetry) {
      throw e;
    }

    // 重试后仍失败，返回具体错误信息
    if (errorMsg.includes("请求超时")) {
      return "请求超时";
    } else if (errorMsg.includes("rate limit") || errorMsg.includes("API速率限制")) {
      return "API限制";
    } else if (errorMsg.includes("temporarily unavailable") || errorMsg.includes("临时不可用")) {
      return "API不可用";
    }
    return "网络错误";
  }
};

// 检查是否为币安Web3钱包（带缓存和重试）
const checkAddress = async (address: string, preferredProvider: APIProvider = "moralis"): Promise<string> => {
  // 检查缓存
  if (addressCache[address]) return addressCache[address];

  try {
    // 第一次尝试
    const result = await checkAddressInternal(address, false, preferredProvider);
    addressCache[address] = result;
    return result;
  } catch (e) {
    // 第一次失败，等待后重试
    await delay(RETRY_DELAY); // 等待3秒后重试

    try {
      const result = await checkAddressInternal(address, true, preferredProvider);
      addressCache[address] = result;
      return result;
    } catch (retryError) {
      // 重试也失败
      const result = "查询失败";
      addressCache[address] = result;
      return result;
    }
  }
};

// 查询某地址的最近关联交易
const getRelatedAddresses = async (address: string, preferredProvider: APIProvider = "moralis") => {
  try {
    const { transactions } = await getTransactions(address, preferredProvider);

    const result = transactions
      .map((tx: any) => {
        // 兼容 Moralis 的 from_address/to_address 和 Etherscan 的 from/to
        const fromAddr = tx.from_address || tx.from;
        const toAddr = tx.to_address || tx.to;

        // 检查必要字段是否存在
        if (!fromAddr || !toAddr || !tx.value) return null;

        // Moralis 的 value 可能是字符串，需要转换
        let valueWei: number;
        if (typeof tx.value === 'string') {
          valueWei = parseFloat(tx.value);
        } else {
          valueWei = tx.value;
        }

        const valueBNB = valueWei / 1e18;

        if (valueBNB <= MIN_BNB_THRESHOLD) return null;

        return {
          from: fromAddr,
          to: toAddr,
          value: valueBNB.toFixed(4),
          hash: tx.hash || tx.transaction_hash || "", // 添加交易hash
          direction: fromAddr.toLowerCase() === address.toLowerCase() ? "转出" : "转入",
        };
      })
      .filter(Boolean);

    return result;
  } catch (error) {
    console.error(`获取地址 ${address} 的关联交易失败:`, error);
    return [];
  }
};

// Moralis API: 获取ERC20转账记录
const getERC20TransfersFromMoralis = async (address: string, tokenAddress: string): Promise<any[]> => {
  const url = `https://deep-index.moralis.io/api/v2/${address}/erc20/transfers?chain=bsc&contract_addresses[]=${tokenAddress}&limit=${TX_COUNT}`;

  const response = await fetchWithTimeout(url, {
    headers: {
      'X-API-Key': MORALIS_API_KEY,
      'Accept': 'application/json'
    }
  });

  if (!response.ok) {
    throw new Error(`Moralis API错误: ${response.status}`);
  }

  const data = await response.json();

  // Moralis 返回格式: { result: [...] }
  if (!data.result || !Array.isArray(data.result)) {
    throw new Error("Moralis API返回数据异常");
  }

  return data.result;
};

// Etherscan API: 获取ERC20转账记录
const getERC20TransfersFromEtherscan = async (address: string, tokenAddress: string): Promise<any[]> => {
  const url = `https://api.etherscan.io/v2/api?chainid=56&module=account&action=tokentx&contractaddress=${tokenAddress}&address=${address}&page=1&offset=${TX_COUNT}&sort=desc&apikey=${ETHERSCAN_API_KEY}`;

  const response = await fetchWithTimeout(url);

  if (!response.ok) {
    throw new Error(`Etherscan API错误: ${response.status}`);
  }

  const data = await response.json();

  // 检查API是否返回错误
  if (data.status === "0" && data.message) {
    if (data.message.includes("rate limit") || data.message.includes("Max rate")) {
      throw new Error("API速率限制");
    }
    if (data.message === "NOTOK" || data.message.includes("temporarily unavailable")) {
      throw new Error("Etherscan API临时不可用");
    }
  }

  if (!data.result || !Array.isArray(data.result)) {
    if (data.status === "0" && typeof data.result === "string") {
      throw new Error(`Etherscan API错误: ${data.result}`);
    }
    throw new Error("Etherscan API返回数据异常");
  }

  return data.result;
};

// 通用ERC20转账获取函数（带API备选机制）
const getERC20Transfers = async (
  address: string,
  tokenAddress: string,
  preferredProvider: APIProvider = "moralis"
): Promise<{ transfers: any[], provider: APIProvider }> => {
  // 先尝试首选API
  try {
    if (preferredProvider === "moralis") {
      const transfers = await getERC20TransfersFromMoralis(address, tokenAddress);
      apiStats.moralis.success++;
      return { transfers, provider: "moralis" };
    } else {
      const transfers = await getERC20TransfersFromEtherscan(address, tokenAddress);
      apiStats.etherscan.success++;
      return { transfers, provider: "etherscan" };
    }
  } catch (error: any) {
    if (preferredProvider === "moralis") {
      apiStats.moralis.failed++;
    } else {
      apiStats.etherscan.failed++;
    }

    // 尝试备选API
    const fallbackProvider = preferredProvider === "moralis" ? "etherscan" : "moralis";
    try {
      if (fallbackProvider === "moralis") {
        const transfers = await getERC20TransfersFromMoralis(address, tokenAddress);
        apiStats.moralis.success++;
        return { transfers, provider: "moralis" };
      } else {
        const transfers = await getERC20TransfersFromEtherscan(address, tokenAddress);
        apiStats.etherscan.success++;
        return { transfers, provider: "etherscan" };
      }
    } catch (fallbackError: any) {
      if (fallbackProvider === "moralis") {
        apiStats.moralis.failed++;
      } else {
        apiStats.etherscan.failed++;
      }
      throw new Error(`所有API均失败: ${error.message}, ${fallbackError.message}`);
    }
  }
};

// 查询某地址的ERC20关联交易
const getRelatedERC20Addresses = async (
  address: string,
  tokenType: "USDT" | "USDC",
  preferredProvider: APIProvider = "moralis"
) => {
  try {
    const tokenConfig = TOKEN_CONTRACTS[tokenType];
    const { transfers } = await getERC20Transfers(address, tokenConfig.address, preferredProvider);

    const result = transfers
      .map((tx: any) => {
        // Moralis和Etherscan的字段名不同，需要兼容处理
        // Moralis: from_address, to_address, value
        // Etherscan: from, to, value
        const from = tx.from_address || tx.from;
        const to = tx.to_address || tx.to;
        const value = tx.value;
        const hash = tx.transaction_hash || tx.hash || "";

        if (!from || !to || !value) return null;

        // 将value从wei转换为token数量
        const tokenAmount = parseFloat(value) / Math.pow(10, tokenConfig.decimals);

        // 过滤小于阈值的交易
        if (tokenAmount < MIN_TOKEN_THRESHOLD) return null;

        return {
          from,
          to,
          value: tokenAmount.toFixed(2),
          hash,
          direction: from.toLowerCase() === address.toLowerCase() ? "转出" : "转入",
          tokenSymbol: tokenConfig.symbol,
        };
      })
      .filter(Boolean);

    return result;
  } catch (error) {
    console.error(`获取地址 ${address} 的ERC20转账记录失败:`, error);
    return [];
  }
};

// 导出ERC20资金流格式
const exportERC20ToFlowFormat = (rows: any[]) => {
  if (!rows.length) {
    message.warning("没有可导出的数据");
    return;
  }

  const flowLines = rows.map((r) => {
    const from = r.direction === "转出" ? r.source : r.related;
    const to = r.direction === "转出" ? r.related : r.source;
    return `${from}----${r.amount}${r.tokenSymbol}----${to}`;
  });

  const content = flowLines.join("\n");
  navigator.clipboard.writeText(content).then(() => {
    message.success(`已复制 ${rows.length} 条资金流记录到剪贴板`);
  }).catch(() => {
    message.error("复制失败");
  });
};

// 导出ERC20详细CSV
const exportERC20DetailedCSV = (rows: any[]) => {
  if (!rows.length) {
    message.warning("没有可导出的数据");
    return;
  }

  const header = ["From地址", "To地址", "金额", "代币", "交易Hash", "是否币安Web3钱包"];
  const csvRows = [
    header.join(","),
    ...rows.map((r) => {
      const from = r.direction === "转出" ? r.source : r.related;
      const to = r.direction === "转出" ? r.related : r.source;
      return `${from},${to},${r.amount},${r.tokenSymbol},${r.hash || "无"},${r.isWeb3}`;
    }),
  ];

  const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "erc20_fund_flow_detailed.csv";
  a.click();
  URL.revokeObjectURL(url);
  message.success("详细CSV导出成功");
};

// 导出 CSV 工具函数（修改为包含hash）
const exportToCSV = (rows: any[], mode: "related" | "direct") => {
  if (!rows.length) {
    message.warning("没有可导出的数据");
    return;
  }

  let header: string[];
  let csvRows: string[];

  if (mode === "related") {
    header = ["初始地址", "关联地址", "方向", "金额(BNB)", "交易Hash", "是否币安Web3钱包"];
    csvRows = [
      header.join(","),
      ...rows.map(
        (r) =>
          `${r.source},${r.related},${r.direction},${r.amount},${r.hash || "无"},${r.isWeb3}`
      ),
    ];
  } else {
    header = ["地址", "是否币安Web3钱包"];
    csvRows = [
      header.join(","),
      ...rows.map((r) => `${r.address},${r.isWeb3}`),
    ];
  }

  const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "binance_web3_results.csv";
  a.click();
  URL.revokeObjectURL(url);
};

// 导出为资金流格式（用于可视化工具）
const exportToFlowFormat = (rows: any[]) => {
  if (!rows.length) {
    message.warning("没有可导出的数据");
    return;
  }

  // 格式：from----amountBNB----to
  const flowLines = rows.map((r) => {
    const from = r.direction === "转出" ? r.source : r.related;
    const to = r.direction === "转出" ? r.related : r.source;
    return `${from}----${r.amount}BNB----${to}`;
  });

  const content = flowLines.join("\n");
  navigator.clipboard.writeText(content).then(() => {
    message.success(`已复制 ${rows.length} 条资金流记录到剪贴板（格式：地址----金额BNB----地址）`);
  }).catch(() => {
    message.error("复制失败");
  });
};

// 导出详细CSV（包含完整hash信息）
const exportDetailedCSV = (rows: any[]) => {
  if (!rows.length) {
    message.warning("没有可导出的数据");
    return;
  }

  // 重新组织数据为 From -> To 的流向格式
  const header = ["From地址", "To地址", "金额(BNB)", "交易Hash", "是否币安Web3钱包"];
  const csvRows = [
    header.join(","),
    ...rows.map((r) => {
      const from = r.direction === "转出" ? r.source : r.related;
      const to = r.direction === "转出" ? r.related : r.source;
      return `${from},${to},${r.amount},${r.hash || "无"},${r.isWeb3}`;
    }),
  ];

  const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "fund_flow_detailed.csv";
  a.click();
  URL.revokeObjectURL(url);
  message.success("详细CSV导出成功");
};

// 复制格式化文本工具函数
const copyFormattedText = (rows: any[], mode: "related" | "direct") => {
  if (!rows.length) {
    message.warning("没有可复制的数据");
    return;
  }

  let formatted: string;
  if (mode === "related") {
    formatted = rows.map((r) => `${r.source}----${r.related}`).join("\n");
  } else {
    // 直接查询模式：只复制是币安Web3钱包的地址
    const web3Addresses = rows.filter((r) => r.isWeb3 === "是");
    if (web3Addresses.length === 0) {
      message.warning("没有币安Web3钱包地址");
      return;
    }
    formatted = web3Addresses.map((r) => r.address).join("\n");
  }

  navigator.clipboard.writeText(formatted).then(() => {
    message.success("已复制到剪贴板");
  }).catch(() => {
    message.error("复制失败");
  });
};

const BinanceWalletChecker: React.FC = () => {
  const [input, setInput] = useState("");
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [queryMode, setQueryMode] = useState<"related" | "direct">("related");
  const [apiProvider, setApiProvider] = useState<APIProvider>("moralis");

  // ERC20相关状态
  const [erc20Input, setErc20Input] = useState("");
  const [erc20DataSource, setErc20DataSource] = useState<any[]>([]);
  const [erc20Loading, setErc20Loading] = useState(false);
  const [selectedToken, setSelectedToken] = useState<"USDT" | "USDC">("USDT");
  const [erc20QueryMode, setErc20QueryMode] = useState<"related" | "direct">("related");

  // 重置API统计
  const resetApiStats = () => {
    apiStats = {
      moralis: { success: 0, failed: 0 },
      etherscan: { success: 0, failed: 0 }
    };
  };

  // 获取API统计信息
  const getApiStatsText = () => {
    const total = apiStats.moralis.success + apiStats.moralis.failed + apiStats.etherscan.success + apiStats.etherscan.failed;
    if (total === 0) return "";

    return `API统计 - Moralis: ${apiStats.moralis.success}成功/${apiStats.moralis.failed}失败, Etherscan: ${apiStats.etherscan.success}成功/${apiStats.etherscan.failed}失败`;
  };

  // 关联查询模式（原有逻辑）
  const handleRelatedCheck = async () => {
    const addresses = input
      .split("\n")
      .map((a) => a.trim())
      .filter((a) => ETH_ADDRESS_REGEX.test(a));

    if (addresses.length === 0) {
      message.error("请输入有效的钱包地址");
      return;
    }

    setLoading(true);
    resetApiStats(); // 重置统计
    const resultRows: any[] = [];
    const checkedCounterparties = new Set<string>(); // 已检测的地址集合

    for (const addr of addresses) {
      const related = await getRelatedAddresses(addr, apiProvider);
      await delay(REQUEST_DELAY); // 添加请求间隔

      for (const r of related) {
        if (!r) continue; // Skip null values (TypeScript type guard)
        const counterparty = r.direction === "转出" ? r.to : r.from;

        // 确保对方地址存在
        if (!counterparty) continue;

        const normalized = counterparty.toLowerCase();

        // 跳过重复检测的地址
        if (checkedCounterparties.has(normalized)) {
          continue;
        }
        checkedCounterparties.add(normalized);

        const isWeb3 = await checkAddress(counterparty, apiProvider);
        await delay(REQUEST_DELAY); // 添加请求间隔

        // 仅保留币安Web3钱包结果
        if (isWeb3 === "是") {
          resultRows.push({
            key: `${addr}-${counterparty}-${r.value}`,
            source: addr,
            related: counterparty,
            direction: r.direction,
            amount: r.value,
            hash: r.hash || "", // 添加交易hash
            isWeb3,
          });
          setDataSource([...resultRows]);
        }
      }
    }

    message.success(`查询完成 - ${getApiStatsText()}`);
    setLoading(false);
  };

  // 直接批量查询模式（新增）
  const handleDirectCheck = async () => {
    const addresses = input
      .split("\n")
      .map((a) => a.trim())
      .filter((a) => ETH_ADDRESS_REGEX.test(a));

    if (addresses.length === 0) {
      message.error("请输入有效的钱包地址");
      return;
    }

    setLoading(true);
    resetApiStats(); // 重置统计
    setDataSource([]); // 清空之前的结果
    const resultRows: any[] = [];

    for (let i = 0; i < addresses.length; i++) {
      const addr = addresses[i];

      // 添加请求间隔（第一个请求不需要延迟）
      if (i > 0) {
        await delay(REQUEST_DELAY);
      }

      const isWeb3 = await checkAddress(addr, apiProvider);

      resultRows.push({
        key: addr,
        address: addr,
        isWeb3,
      });

      // 实时更新表格
      setDataSource([...resultRows]);
    }

    message.success(`查询完成，共检测 ${addresses.length} 个地址 - ${getApiStatsText()}`);
    setLoading(false);
  };

  const handleCheck = async () => {
    if (queryMode === "related") {
      await handleRelatedCheck();
    } else {
      await handleDirectCheck();
    }
  };

  const handleModeChange = (e: RadioChangeEvent) => {
    setQueryMode(e.target.value);
    setDataSource([]); // 切换模式时清空结果
  };

  // ERC20关联查询模式
  const handleERC20RelatedCheck = async () => {
    const addresses = erc20Input
      .split("\n")
      .map((a) => a.trim())
      .filter((a) => ETH_ADDRESS_REGEX.test(a));

    if (addresses.length === 0) {
      message.error("请输入有效的钱包地址");
      return;
    }

    setErc20Loading(true);
    resetApiStats();
    const resultRows: any[] = [];
    const checkedCounterparties = new Set<string>();

    for (const addr of addresses) {
      const related = await getRelatedERC20Addresses(addr, selectedToken, apiProvider);
      await delay(REQUEST_DELAY);

      for (const r of related) {
        if (!r) continue;
        const counterparty = r.direction === "转出" ? r.to : r.from;

        if (!counterparty) continue;

        const normalized = counterparty.toLowerCase();

        if (checkedCounterparties.has(normalized)) {
          continue;
        }
        checkedCounterparties.add(normalized);

        const isWeb3 = await checkAddress(counterparty, apiProvider);
        await delay(REQUEST_DELAY);

        if (isWeb3 === "是") {
          resultRows.push({
            key: `${addr}-${counterparty}-${r.value}`,
            source: addr,
            related: counterparty,
            direction: r.direction,
            amount: r.value,
            hash: r.hash || "",
            tokenSymbol: r.tokenSymbol,
            isWeb3,
          });
          setErc20DataSource([...resultRows]);
        }
      }
    }

    message.success(`ERC20查询完成 - ${getApiStatsText()}`);
    setErc20Loading(false);
  };

  // ERC20直接批量查询模式
  const handleERC20DirectCheck = async () => {
    const addresses = erc20Input
      .split("\n")
      .map((a) => a.trim())
      .filter((a) => ETH_ADDRESS_REGEX.test(a));

    if (addresses.length === 0) {
      message.error("请输入有效的钱包地址");
      return;
    }

    setErc20Loading(true);
    resetApiStats();
    setErc20DataSource([]);
    const resultRows: any[] = [];

    for (let i = 0; i < addresses.length; i++) {
      const addr = addresses[i];

      if (i > 0) {
        await delay(REQUEST_DELAY);
      }

      const isWeb3 = await checkAddress(addr, apiProvider);

      resultRows.push({
        key: addr,
        address: addr,
        isWeb3,
      });

      setErc20DataSource([...resultRows]);
    }

    message.success(`查询完成，共检测 ${addresses.length} 个地址 - ${getApiStatsText()}`);
    setErc20Loading(false);
  };

  const handleERC20Check = async () => {
    if (erc20QueryMode === "related") {
      await handleERC20RelatedCheck();
    } else {
      await handleERC20DirectCheck();
    }
  };

  const handleERC20ModeChange = (e: RadioChangeEvent) => {
    setErc20QueryMode(e.target.value);
    setErc20DataSource([]);
  };

  // 根据查询模式动态定义列
  const relatedColumns = [
    { title: "初始地址(中转)", dataIndex: "source", key: "source", ellipsis: true },
    { title: "关联地址(目标)", dataIndex: "related", key: "related", ellipsis: true },
    { title: "方向", dataIndex: "direction", key: "direction" },
    { title: "金额(BNB)", dataIndex: "amount", key: "amount" },
    {
      title: "交易Hash",
      dataIndex: "hash",
      key: "hash",
      ellipsis: true,
      width: 120,
      render: (hash: string) => (
        <Tooltip title={hash || "无"}>
          <span style={{ fontSize: 12, color: "#666" }}>
            {hash ? `${hash.slice(0, 10)}...${hash.slice(-8)}` : "无"}
          </span>
        </Tooltip>
      )
    },
    {
      title: "币安Web3钱包",
      dataIndex: "isWeb3",
      key: "isWeb3",
      render: (v: string) =>
        v === "是" ? (
          <span style={{ color: "green", fontWeight: 600 }}>✅ 是</span>
        ) : null,
    },
  ];

  const directColumns = [
    {
      title: "地址",
      dataIndex: "address",
      key: "address",
      ellipsis: true,
      width: "70%"
    },
    {
      title: "是否币安Web3钱包",
      dataIndex: "isWeb3",
      key: "isWeb3",
      render: (v: string) => {
        if (v === "是") {
          return <span style={{ color: "green", fontWeight: 600 }}>✅ 是</span>;
        } else if (v === "否") {
          return <span style={{ color: "red" }}>❌ 否</span>;
        } else if (v === "无交易记录") {
          return <span style={{ color: "orange" }}>⚠️ 无交易记录</span>;
        } else if (v === "查询失败") {
          return <span style={{ color: "red" }}>⚠️ 查询失败（请重试）</span>;
        } else if (v === "请求超时") {
          return <span style={{ color: "red" }}>⏱️ 请求超时（已重试2次）</span>;
        } else if (v === "API限制") {
          return <span style={{ color: "orange" }}>🚫 API速率限制（请稍后重试）</span>;
        } else if (v === "格式错误") {
          return <span style={{ color: "red" }}>❌ 格式错误</span>;
        } else if (v === "网络错误") {
          return <span style={{ color: "red" }}>⚠️ 网络错误（已重试2次）</span>;
        } else {
          return <span style={{ color: "gray" }}>{v}</span>;
        }
      },
    },
  ];

  const columns = queryMode === "related" ? relatedColumns : directColumns;

  // ERC20表格列定义
  const erc20RelatedColumns = [
    { title: "初始地址(中转)", dataIndex: "source", key: "source", ellipsis: true },
    { title: "关联地址(目标)", dataIndex: "related", key: "related", ellipsis: true },
    { title: "方向", dataIndex: "direction", key: "direction" },
    {
      title: "金额",
      dataIndex: "amount",
      key: "amount",
      render: (amount: string, record: any) => `${amount} ${record.tokenSymbol}`
    },
    {
      title: "交易Hash",
      dataIndex: "hash",
      key: "hash",
      ellipsis: true,
      width: 120,
      render: (hash: string) => (
        <Tooltip title={hash || "无"}>
          <span style={{ fontSize: 12, color: "#666" }}>
            {hash ? `${hash.slice(0, 10)}...${hash.slice(-8)}` : "无"}
          </span>
        </Tooltip>
      )
    },
    {
      title: "币安Web3钱包",
      dataIndex: "isWeb3",
      key: "isWeb3",
      render: (v: string) =>
        v === "是" ? (
          <span style={{ color: "green", fontWeight: 600 }}>✅ 是</span>
        ) : null,
    },
  ];

  const erc20DirectColumns = [
    {
      title: "地址",
      dataIndex: "address",
      key: "address",
      ellipsis: true,
      width: "70%"
    },
    {
      title: "是否币安Web3钱包",
      dataIndex: "isWeb3",
      key: "isWeb3",
      render: (v: string) => {
        if (v === "是") {
          return <span style={{ color: "green", fontWeight: 600 }}>✅ 是</span>;
        } else if (v === "否") {
          return <span style={{ color: "red" }}>❌ 否</span>;
        } else if (v === "无交易记录") {
          return <span style={{ color: "orange" }}>⚠️ 无交易记录</span>;
        } else if (v === "查询失败") {
          return <span style={{ color: "red" }}>⚠️ 查询失败（请重试）</span>;
        } else if (v === "请求超时") {
          return <span style={{ color: "red" }}>⏱️ 请求超时（已重试2次）</span>;
        } else if (v === "API限制") {
          return <span style={{ color: "orange" }}>🚫 API速率限制（请稍后重试）</span>;
        } else if (v === "格式错误") {
          return <span style={{ color: "red" }}>❌ 格式错误</span>;
        } else if (v === "网络错误") {
          return <span style={{ color: "red" }}>⚠️ 网络错误（已重试2次）</span>;
        } else {
          return <span style={{ color: "gray" }}>{v}</span>;
        }
      },
    },
  ];

  const erc20Columns = erc20QueryMode === "related" ? erc20RelatedColumns : erc20DirectColumns;

  return (
    <Card
      title={
        <Space>
          币安Web3钱包检测工具
          <Tooltip title="支持BNB转账和ERC20代币转账的关联性分析">
            <QuestionCircleOutlined />
          </Tooltip>
        </Space>
      }
    >
      <Tabs
        defaultActiveKey="bnb"
        items={[
          {
            key: "bnb",
            label: "BNB转账关联性",
            children: (
              <Space direction="vertical" style={{ width: "100%" }} size="large">
                {/* API 统计提示 */}
                {getApiStatsText() && !loading && (
                  <Alert
                    message={getApiStatsText()}
                    type="info"
                    showIcon
                    closable
                  />
                )}

                {/* API 提供商选择 */}
                <div>
                  <div style={{ marginBottom: 8, fontWeight: 500 }}>API 提供商：</div>
                  <Radio.Group value={apiProvider} onChange={(e) => setApiProvider(e.target.value)} disabled={loading}>
                    <Radio.Button value="moralis">
                      Moralis (推荐)
                    </Radio.Button>
                    <Radio.Button value="etherscan">
                      Etherscan (备用)
                    </Radio.Button>
                  </Radio.Group>
                  <div style={{ marginTop: 4, fontSize: 12, color: "#888" }}>
                    系统会自动在主API失败时切换到备用API
                  </div>
                </div>

                {/* 查询模式选择 */}
                <div>
                  <div style={{ marginBottom: 8, fontWeight: 500 }}>查询模式：</div>
                  <Radio.Group value={queryMode} onChange={handleModeChange}>
                    <Radio.Button value="related">关联地址分析</Radio.Button>
                    <Radio.Button value="direct">直接批量查询</Radio.Button>
                  </Radio.Group>
                  <div style={{ marginTop: 4, fontSize: 12, color: "#888" }}>
                    {queryMode === "related"
                      ? "查询中转地址的最近关联交易，仅展示交易额>1BNB 且对方为币安Web3钱包的地址"
                      : "直接批量检测给定地址是否为币安Web3钱包"}
                  </div>
                </div>

                {/* 输入框 */}
                <TextArea
                  placeholder={
                    queryMode === "related"
                      ? "请输入中转地址（每行一个）\n例如：\n0x1234...\n0x5678..."
                      : "请输入要检测的地址（每行一个）\n例如：\n0x1234...\n0x5678..."
                  }
                  rows={6}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                />

                {/* 操作按钮 */}
                <Space wrap>
                  <Button type="primary" onClick={handleCheck} loading={loading}>
                    {loading ? "查询中..." : "开始检测"}
                  </Button>
                  {queryMode === "related" && (
                    <>
                      <Button
                        type="default"
                        style={{ backgroundColor: "#52c41a", color: "white", borderColor: "#52c41a" }}
                        icon={<CopyOutlined />}
                        onClick={() => exportToFlowFormat(dataSource)}
                        disabled={!dataSource.length}
                      >
                        复制资金流格式
                      </Button>
                      <Button
                        icon={<DownloadOutlined />}
                        onClick={() => exportDetailedCSV(dataSource)}
                        disabled={!dataSource.length}
                      >
                        导出详细CSV
                      </Button>
                    </>
                  )}
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={() => exportToCSV(dataSource, queryMode)}
                    disabled={!dataSource.length}
                  >
                    导出基础CSV
                  </Button>
                  <Button
                    icon={<CopyOutlined />}
                    onClick={() => copyFormattedText(dataSource, queryMode)}
                    disabled={!dataSource.length}
                  >
                    {queryMode === "related" ? "复制地址对" : "复制Web3地址"}
                  </Button>
                  <Button onClick={() => setDataSource([])} disabled={loading}>
                    清空结果
                  </Button>
                </Space>

                {/* 结果表格 */}
                <Spin spinning={loading}>
                  <Table
                    columns={columns}
                    dataSource={dataSource}
                    size="small"
                    bordered
                    pagination={{ pageSize: 10 }}
                  />
                </Spin>
              </Space>
            ),
          },
          {
            key: "erc20",
            label: "ERC20转账关联性",
            children: (
              <Space direction="vertical" style={{ width: "100%" }} size="large">
                {/* API 统计提示 */}
                {getApiStatsText() && !erc20Loading && (
                  <Alert
                    message={getApiStatsText()}
                    type="info"
                    showIcon
                    closable
                  />
                )}

                {/* 代币选择 */}
                <div>
                  <div style={{ marginBottom: 8, fontWeight: 500 }}>选择代币：</div>
                  <Select
                    value={selectedToken}
                    onChange={setSelectedToken}
                    style={{ width: 200 }}
                    disabled={erc20Loading}
                  >
                    <Select.Option value="USDT">USDT (BSC)</Select.Option>
                    <Select.Option value="USDC">USDC (BSC)</Select.Option>
                  </Select>
                  <div style={{ marginTop: 4, fontSize: 12, color: "#888" }}>
                    仅显示金额 &gt; {MIN_TOKEN_THRESHOLD} {selectedToken} 的转账
                  </div>
                </div>

                {/* API 提供商选择 */}
                <div>
                  <div style={{ marginBottom: 8, fontWeight: 500 }}>API 提供商：</div>
                  <Radio.Group value={apiProvider} onChange={(e) => setApiProvider(e.target.value)} disabled={erc20Loading}>
                    <Radio.Button value="moralis">
                      Moralis (推荐)
                    </Radio.Button>
                    <Radio.Button value="etherscan">
                      Etherscan (备用)
                    </Radio.Button>
                  </Radio.Group>
                  <div style={{ marginTop: 4, fontSize: 12, color: "#888" }}>
                    系统会自动在主API失败时切换到备用API
                  </div>
                </div>

                {/* 查询模式选择 */}
                <div>
                  <div style={{ marginBottom: 8, fontWeight: 500 }}>查询模式：</div>
                  <Radio.Group value={erc20QueryMode} onChange={handleERC20ModeChange}>
                    <Radio.Button value="related">关联地址分析</Radio.Button>
                    <Radio.Button value="direct">直接批量查询</Radio.Button>
                  </Radio.Group>
                  <div style={{ marginTop: 4, fontSize: 12, color: "#888" }}>
                    {erc20QueryMode === "related"
                      ? `查询中转地址的最近${selectedToken}转账，仅展示交易额>${MIN_TOKEN_THRESHOLD} 且对方为币安Web3钱包的地址`
                      : "直接批量检测给定地址是否为币安Web3钱包"}
                  </div>
                </div>

                {/* 输入框 */}
                <TextArea
                  placeholder={
                    erc20QueryMode === "related"
                      ? "请输入中转地址（每行一个）\n例如：\n0x1234...\n0x5678..."
                      : "请输入要检测的地址（每行一个）\n例如：\n0x1234...\n0x5678..."
                  }
                  rows={6}
                  value={erc20Input}
                  onChange={(e) => setErc20Input(e.target.value)}
                />

                {/* 操作按钮 */}
                <Space wrap>
                  <Button type="primary" onClick={handleERC20Check} loading={erc20Loading}>
                    {erc20Loading ? "查询中..." : "开始检测"}
                  </Button>
                  {erc20QueryMode === "related" && (
                    <>
                      <Button
                        type="default"
                        style={{ backgroundColor: "#52c41a", color: "white", borderColor: "#52c41a" }}
                        icon={<CopyOutlined />}
                        onClick={() => exportERC20ToFlowFormat(erc20DataSource)}
                        disabled={!erc20DataSource.length}
                      >
                        复制资金流格式
                      </Button>
                      <Button
                        icon={<DownloadOutlined />}
                        onClick={() => exportERC20DetailedCSV(erc20DataSource)}
                        disabled={!erc20DataSource.length}
                      >
                        导出详细CSV
                      </Button>
                    </>
                  )}
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={() => exportToCSV(erc20DataSource, erc20QueryMode)}
                    disabled={!erc20DataSource.length}
                  >
                    导出基础CSV
                  </Button>
                  <Button
                    icon={<CopyOutlined />}
                    onClick={() => copyFormattedText(erc20DataSource, erc20QueryMode)}
                    disabled={!erc20DataSource.length}
                  >
                    {erc20QueryMode === "related" ? "复制地址对" : "复制Web3地址"}
                  </Button>
                  <Button onClick={() => setErc20DataSource([])} disabled={erc20Loading}>
                    清空结果
                  </Button>
                </Space>

                {/* 结果表格 */}
                <Spin spinning={erc20Loading}>
                  <Table
                    columns={erc20Columns}
                    dataSource={erc20DataSource}
                    size="small"
                    bordered
                    pagination={{ pageSize: 10 }}
                  />
                </Spin>
              </Space>
            ),
          },
        ]}
      />
    </Card>
  );
};

export default BinanceWalletChecker;
